import type { AIProviderId } from './ai-provider'

export const TEXT_MODEL_SELECTION_IDS = [
  'gemini-3.5-flash',
  'gemini-3.6-flash',
  'gemini-3.7-flash',
  'gemini-3.8-flash',
  'gemini-3.5-flash-lite',
  'gemini-3.1-pro-preview',
  'gemini-2.5-pro',
  'gemini-2.5-pro-thinking-1024',
  'grok-4.5',
] as const

export type TextModelSelectionId = typeof TEXT_MODEL_SELECTION_IDS[number]

export interface TextModelOption {
  id: TextModelSelectionId
  provider: AIProviderId
  apiModel: string
  label: string
  description: string
  legacy?: boolean
  thinkingBudget?: number
  supportsSamplingOptions?: boolean
}

export const DEFAULT_FLASH_TEXT_MODEL = 'gemini-3.5-flash' as const

export const TEXT_MODEL_OPTIONS: readonly TextModelOption[] = [
  {
    id: 'gemini-3.5-flash',
    provider: 'gemini',
    apiModel: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    description: '高速・高性能。モデル別の利用枠を活用できます。',
  },
  {
    id: 'gemini-3.6-flash',
    provider: 'gemini',
    apiModel: 'gemini-3.6-flash',
    label: 'Gemini 3.6 Flash',
    description: '最新の高速モデル。3.5 Flashとは別の利用枠です。',
    supportsSamplingOptions: false,
  },
  {
    id: 'gemini-3.7-flash',
    provider: 'gemini',
    apiModel: 'gemini-3.7-flash',
    label: 'Gemini 3.7 Flash',
    description: '複雑な推論やコーディングに強い、本番利用可能なFlashモデルです。',
    supportsSamplingOptions: false,
  },
  {
    id: 'gemini-3.8-flash',
    provider: 'gemini',
    apiModel: 'gemini-3.8-flash',
    label: 'Gemini 3.8 Flash',
    description: '長期的で複雑な処理に強い、本番利用可能な最新Flashモデルです。',
    supportsSamplingOptions: false,
  },
  {
    id: 'gemini-3.5-flash-lite',
    provider: 'gemini',
    apiModel: 'gemini-3.5-flash-lite',
    label: 'Gemini 3.5 Flash-Lite',
    description: '速度と低コストを重視した軽量モデルです。',
    supportsSamplingOptions: false,
  },
  {
    id: 'gemini-3.1-pro-preview',
    provider: 'gemini',
    apiModel: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro Preview',
    description: '品質を重視するプレビューモデルです。',
  },
  {
    id: 'gemini-2.5-pro',
    provider: 'gemini',
    apiModel: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro（旧モデル）',
    description: '2026年10月16日に提供終了予定です。',
    legacy: true,
  },
  {
    id: 'gemini-2.5-pro-thinking-1024',
    provider: 'gemini',
    apiModel: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro（思考量1024・旧モデル）',
    description: '従来のProライト相当。2026年10月16日に提供終了予定です。',
    legacy: true,
    thinkingBudget: 1024,
  },
  {
    id: 'grok-4.5',
    provider: 'xai',
    apiModel: 'grok-4.5',
    label: 'Grok 4.5',
    description: 'xAIの高性能な推論・創作モデルです。',
  },
] as const

export const IMAGE_GENERATION_MODEL = 'gemini-3-pro-image' as const

const PRO_DEFAULT_TOOLS = new Set([
  'consultation',
  'dialogue',
  'scenario',
  'textgame',
])

const THINKING_PROFILE_TOOLS = new Set(['building', 'name', 'scene'])

export function isTextModelSelectionId(value: unknown): value is TextModelSelectionId {
  return typeof value === 'string' &&
    TEXT_MODEL_SELECTION_IDS.includes(value as TextModelSelectionId)
}

export function getDefaultTextModel(
  tool: string,
  provider: AIProviderId = 'gemini'
): TextModelSelectionId {
  if (provider === 'xai') return 'grok-4.5'
  return PRO_DEFAULT_TOOLS.has(tool) ? 'gemini-2.5-pro' : DEFAULT_FLASH_TEXT_MODEL
}

export function getTextModelOptions(
  tool: string,
  provider: AIProviderId = 'gemini'
): readonly TextModelOption[] {
  return TEXT_MODEL_OPTIONS.filter(option =>
    option.provider === provider &&
    (THINKING_PROFILE_TOOLS.has(tool) || option.thinkingBudget === undefined)
  )
}

export function getTextModelOption(id: TextModelSelectionId): TextModelOption {
  return TEXT_MODEL_OPTIONS.find(option => option.id === id) as TextModelOption
}

export function getTextModelProvider(id: TextModelSelectionId): AIProviderId {
  return getTextModelOption(id).provider
}

export function getTextModelProviderByApiModel(apiModel: string): AIProviderId {
  return TEXT_MODEL_OPTIONS.find(option => option.apiModel === apiModel)?.provider ??
    'gemini'
}
